import React, { useEffect, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { getHoldingSettlementResponses } from '../../helper/Api';
import { AddCommasToAmount } from '../../helper/Utils';
import '../../pages/BankSettlement/BankSettlementOnHold.css';
import './HoldingSettlementResponseList.css';

const TABLE_HEADER_HEIGHT = 56;
/** Multi-line cells (ACH / Beneficiary Bank / wrapped names) — not single-line 48px */
const TABLE_ROW_HEIGHT = 100;
const TABLE_PAGINATION_HEIGHT = 56;
const TABLE_CARD_PADDING = 32;
const TABLE_BOTTOM_GAP = 16;

const buildUniqueRowKey = (row, index) =>
    [
        row.id,
        row.holding_settlement_id,
        row.transaction_reference,
        row.beneficiary_account_no,
        row.created_at,
        index,
    ].join('-');

const DateDifference = (createdAt) => {
    const givenDate = moment(createdAt, [
        'YYYY-MM-DD HH:mm:ss',
        moment.ISO_8601,
    ]);
    const today = moment();
    return today.diff(givenDate, 'days');
};

const getPaymentStatus = (row) => {
    const flag = String(row.flag_success_return ?? '').trim();
    const reason = String(row.reason_code ?? '').trim();

    if (flag === '1') {
        return { label: 'Success', tone: 'success' };
    }
    if (flag === '3') {
        return { label: 'On Hold', tone: 'hold' };
    }
    if (flag === '0' || flag === '2') {
        return { label: 'Failed', tone: 'danger' };
    }
    if (reason !== '' && reason !== '00') {
        return { label: 'Failed', tone: 'danger' };
    }
    return { label: 'Pending', tone: 'warning' };
};

const isDaysApplicable = (status) =>
    status.tone === 'warning' || status.tone === 'hold';

const getDaysBadgeTone = (days) => {
    if (days <= 4) return 'primary';
    if (days <= 6) return 'warning';
    return 'danger';
};

const formatAmount = (row) => {
    if (row.amount_paise !== null && row.amount_paise !== undefined && row.amount_paise !== '') {
        return AddCommasToAmount(Number(row.amount_paise) / 100);
    }
    if (row.amount !== null && row.amount !== undefined && row.amount !== '') {
        return AddCommasToAmount(row.amount);
    }
    return '';
};

const HoldingSettlementResponseList = () => {
    const tableContainerRef = useRef(null);
    const { authData } = useSelector((state) => state.authData);
    const departmentId = authData?.user?.department_id;
    const [loading, setLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [records, setRecords] = useState([]);
    const [tableScrollHeight, setTableScrollHeight] = useState('auto');

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 'bold',
                fontSize: '14px',
            },
        },
        rows: {
            style: {
                minHeight: `${TABLE_ROW_HEIGHT}px`,
            },
        },
    };

    const columns = [
        {
            name: 'Sett. Id',
            // Line id (holding_file_details), same as bank settlement beneficiaries
            selector: (row) => row.id,
            width: '100px',
        },
        {
            name: '+ Days',
            cell: (row) => {
                const status = getPaymentStatus(row);
                if (!isDaysApplicable(status)) {
                    return <span className="bank-settlement-days-na">NA</span>;
                }
                const days = DateDifference(row.created_at);
                const badgeTone = getDaysBadgeTone(days);
                return (
                    <span
                        className={`bank-settlement-days-badge bank-settlement-days-badge--${badgeTone}`}
                    >
                        {days}+
                    </span>
                );
            },
            center: true,
            width: '90px',
        },
        {
            name: 'Status',
            cell: (row) => {
                const status = getPaymentStatus(row);
                return (
                    <div className="bank-settlement-status-cell">
                        <span
                            className={`bank-settlement-status-cell__label bank-settlement-status-cell__label--${status.tone}`}
                        >
                            {status.label}
                        </span>
                        {status.tone !== 'warning' &&
                        String(row.reason_code ?? '').trim() ? (
                            <span
                                className={`bank-settlement-status-cell__badge bank-settlement-status-cell__badge--${status.tone}`}
                            >
                                {row.reason_code}
                            </span>
                        ) : null}
                    </div>
                );
            },
            center: true,
            width: '110px',
        },
        {
            name: 'Tran. Ref',
            selector: (row) => row.transaction_reference,
            width: '200px',
        },
        {
            name: 'ACH / Product',
            cell: (row) => (
                <div className="bank-settlement-bank-cell">
                    <div className="bank-settlement-bank-cell__row">
                        <span className="bank-settlement-bank-cell__label">ACH Code:</span>
                        <span className="bank-settlement-bank-cell__value">
                            {row.ach_transaction_code || '-'}
                        </span>
                    </div>
                    <div className="bank-settlement-bank-cell__row">
                        <span className="bank-settlement-bank-cell__label">Product Type:</span>
                        <span className="bank-settlement-bank-cell__value">
                            {row.product_type || '-'}
                        </span>
                    </div>
                </div>
            ),
            wrap: true,
            minWidth: '160px',
        },
        {
            name: 'Bene. Name',
            selector: (row) => row.beneficiary_name,
            wrap: true,
        },
        {
            name: 'User Name',
            selector: (row) => row.user_name,
            wrap: true,
        },
        {
            name: 'Amount',
            cell: (row) => (
                <span className="holding-settlement-response-amount">
                    {formatAmount(row)}
                </span>
            ),
            right: true,
            minWidth: '110px',
        },
        {
            name: 'Beneficiary Bank',
            cell: (row) => (
                <div className="bank-settlement-bank-cell">
                    <div className="bank-settlement-bank-cell__row">
                        <span className="bank-settlement-bank-cell__label">IFSC:</span>
                        <span className="bank-settlement-bank-cell__value">
                            {row.destination_ifsc_micr_iin || '-'}
                        </span>
                    </div>
                    <div className="bank-settlement-bank-cell__row bank-settlement-bank-cell__row--account">
                        <span className="bank-settlement-bank-cell__label">A/C:</span>
                        <span className="bank-settlement-bank-cell__value bank-settlement-bank-cell__value--account">
                            {row.beneficiary_account_no || '-'}
                        </span>
                    </div>
                </div>
            ),
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Sponsor IFSC',
            cell: (row) => (
                <span className="bank-settlement-sponsor-ifsc">
                    {row.sponsor_ifsc_micr_iin || '-'}
                </span>
            ),
            wrap: true,
            minWidth: '130px',
        },
        {
            name: 'User Number',
            selector: (row) => row.user_number,
            wrap: true,
        },
        {
            name: 'Created At',
            selector: (row) => row.created_at,
            wrap: true,
            minWidth: '170px',
        },
    ];

    const fetchResponses = async () => {
        try {
            setLoading(true);
            const res = await getHoldingSettlementResponses({
                department_id: departmentId,
            });
            if (res.data.status) {
                const list = res.data.data || res.data.result || [];
                setRecords(
                    list.map((row, index) => ({
                        ...row,
                        rowKey: buildUniqueRowKey(row, index),
                    }))
                );
            } else {
                setRecords([]);
            }
        } catch (_error) {
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchResponses();
    }, [departmentId]);

    useEffect(() => {
        const updateTableHeight = () => {
            const rowCount = Math.min(records.length || 1, rowsPerPage);
            // Multi-line cells are taller than default RDT rows — size from that
            const bodyContentHeight = rowCount * TABLE_ROW_HEIGHT + 16;
            const containerTop =
                tableContainerRef.current?.getBoundingClientRect().top ?? 220;
            const maxBodyHeight =
                window.innerHeight -
                containerTop -
                TABLE_HEADER_HEIGHT -
                TABLE_PAGINATION_HEIGHT -
                TABLE_CARD_PADDING -
                TABLE_BOTTOM_GAP;

            // Few rows that fit the viewport: grow with content (no tiny inner scroll)
            // Many rows: cap to viewport and scroll inside the table
            if (bodyContentHeight <= Math.max(maxBodyHeight, 200)) {
                setTableScrollHeight(`${bodyContentHeight}px`);
            } else {
                setTableScrollHeight(`${Math.max(maxBodyHeight, 200)}px`);
            }
        };

        const raf = window.requestAnimationFrame(updateTableHeight);
        window.addEventListener('resize', updateTableHeight);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', updateTableHeight);
        };
    }, [records.length, rowsPerPage, loading]);

    const useFixedHeader = records.length > 0;

    return (
        <div
            ref={tableContainerRef}
            className="holding-settlement-response-datatable-root card p-3"
        >
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                columns={columns}
                data={records}
                keyField="rowKey"
                progressPending={loading}
                fixedHeader={useFixedHeader}
                fixedHeaderScrollHeight={tableScrollHeight}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={records.length}
                paginationRowsPerPageOptions={[100, 200, 500, 1000]}
                onChangeRowsPerPage={setRowsPerPage}
                noDataComponent={
                    <div className="p-4 text-muted">
                        No holding settlement response records found.
                    </div>
                }
            />
        </div>
    );
};

export default HoldingSettlementResponseList;
